/* global GM_STATE */
import { useEffect, useMemo, useState } from 'preact/hooks'
import Icon from 'snippets/icon/icon'
import register from 'preact-custom-element'
import clsx from 'clsx'

const SIZE_CATEGORY_KEYS = {
  BABY: 'Baby',
  TODDLER: 'Toddler',
  '3-8_YEARS': '3-8 Yrs',
  '8+_YEARS': '8+ Yrs'
}

export const sizeTypes = [
  { value: 'UK', label: 'UK' },
  { value: 'US', label: 'US' },
  { value: 'EURO', label: 'EU' },
  { value: 'CM', label: 'CM' }
]

const DEFAULT_WIDTH_IF_PRESENT = 'E'
const SORT_SIZE_CATEGORY_KEYS = Object.values(SIZE_CATEGORY_KEYS)

export const getSizeGroupFromRange = (size) => {
  if (size < 20) {
    return SIZE_CATEGORY_KEYS.BABY
  }
  if (size >= 20 && size < 28) {
    return SIZE_CATEGORY_KEYS.TODDLER
  }
  if (size >= 28 && size < 35) {
    return SIZE_CATEGORY_KEYS['3-8_YEARS']
  }
  if (size >= 35) {
    return SIZE_CATEGORY_KEYS['8+_YEARS']
  }
}

const handleSelectedVariantParam = (id) => {
  const variantSelectedEvent = new Event('variant-selected', { bubbles: true, cancelable: true })
  window.history.replaceState({}, '', `?variant=${id}`)
  window.dispatchEvent(variantSelectedEvent)
}
const handleGetTypeValue = (type, size) => {
  switch (type) {
    case 'UK': {
      return size.uk
    }
    case 'US': {
      return size.us
    }
    case 'EURO': {
      return size.eu
    }
    case 'CM': {
      return size.cm
    }
  }
}

/**
 * Parses the product variant data into groups based on its category and or width
 *
 * DataStructure
 *
 * A) Without widths
 * {
 *  [category: string] {
 *    [id] : string
      [name] : string
      [available] : boolean
      [age_group] : string
      [cm] : string
      [uk] : string
      [us] : string
      [eu] : string
 *  }
 * }
 *  *
 * B) With widths
 * {
 *  [category: string] {
 *    [width: string] {
 *    [id] : string
      [name] : string
      [available] : boolean
      [age_group] : string
      [cm] : string
      [uk] : string
      [us] : string
      [eu] : string }
    }
 * }
 * @param {*} variants
 * @returns
 */
const sizeGroups = (variants) => {
  const s = variants.reduce((acc, v) => {
    const group = v.age_group
    if (v?.width) {
      if (acc[group]) {
        if (acc[group][v.width]) {
          acc[group][v.width].push(v)
          return acc
        }
      }
      acc[group] = {
        ...acc[group],
        [v.width]: [v]
      }
      return acc
    }

    if (acc[group]) {
      acc[group].push(v)
      return acc
    }
    acc[group] = [v]
    return acc
  }, {})

  return s
}

export const parseSizeType = (c, type) => {
  return c.name.split('/').filter((t) => t.includes(type))
}

export const getSizeFromTitle = (row, type) => {
  const inputType = type === 'CM' ? 'EURO' : type
  const input = Array.isArray(row) ? row.pop() : row
  const name = parseSizeType(input, inputType)
  if (!name.length) {
    return '-'
  }
  const value = name.pop().trimEnd().split(' ').pop()
  if (type === 'CM') return (parseFloat(value) * 2.54).toFixed(2)
  return value
}

/**
 *
 * @param {*} param0
 * @returns
 */
const AvailableOption = ({ size, handleSetSelectedCallback, isSelected, productId }) => {
  const handleSelectSize = () => {
    handleSetSelectedCallback({
      id: size.id,
      name: size.name,
      value: size.value,
      width: size?.width,
      variant: size.variant,
      unavailable_stock: false
    })
    handleSelectedVariantParam(size.id)
  }
  return (
    <div
      data-dl-track="toggle_variant_pdp"
      data-dl-attribute="item_size"
      data-dl-value={size.value}
      data-dl-product-id={productId}
      onClick={handleSelectSize}
      class={`flex h-12 w-18 cursor-pointer flex-col items-center justify-center rounded-md border border-solid ${
        isSelected ? 'bg-primary-blue text-white' : 'border-neutrals-tertiary-grey'
      } `}
    >
      <span>{size.value}</span>
    </div>
  )
}

const UnavailableOption = ({ size, handleSetSelectedCallback, isSelected, productId }) => {
  const handleSelectSize = () => {
    handleSetSelectedCallback({
      id: size.id,
      name: size.name,
      width: size?.width,
      value: size.value,
      variant: size.variant,
      unavailable_stock: true
    })
    handleSelectedVariantParam(size.id)
  }
  return (
    <div
      data-dl-track="toggle_variant_pdp"
      data-dl-attribute="item_size"
      data-dl-value={size.value}
      data-dl-product-id={productId}
      onClick={handleSelectSize}
      class={`relative flex h-12 w-18 cursor-pointer flex-col items-center justify-center rounded-md border border-solid ${
        isSelected ? 'border-black bg-neutrals-medium-grey/70' : 'bg-neutrals-medium-grey/50'
      } `}
    >
      {GM_STATE.apps.klaviyo.backInStock.enabled && (
        <div className={'absolute -right-1 -top-1 '}>
          <div class={`aspect-1 w-4 rounded-full ${isSelected ? 'bg-black' : 'bg-secondary-yellow'} `}>
            <Icon id="notification" className={'h-4 w-4 scale-95 text-white'} />
          </div>
        </div>
      )}
      <s className={`${isSelected ? '' : 'opacity-50'}`}>{size.value}</s>
    </div>
  )
}

const SelectorSize = ({ type, option, handleSetSelectedCallback, selected, productId }) => {
  const size = useMemo(() => {
    const name = getSizeFromTitle(option, type)
    const id = option.id
    const available = option.available
    const getSizeValue = handleGetTypeValue(type, option)
    const value = !getSizeValue ? option.name : getSizeValue
    return { name, id, available, width: option.width, variant: option.name, value }
  }, [type, selected])

  if (!size.value) return <></>

  if (size.available === false) {
    return (
      <UnavailableOption
        productId={productId}
        size={size}
        handleSetSelectedCallback={handleSetSelectedCallback}
        isSelected={size.id === selected.id}
      />
    )
  }
  return (
    <AvailableOption
      productId={productId}
      size={size}
      handleSetSelectedCallback={handleSetSelectedCallback}
      isSelected={size.id === selected.id}
    />
  )
}

function SelectCountrySizeCategory({ validHeaders, current, setTypeCallback }) {
  return (
    <div className={'flex flex-row gap-2'}>
      {sizeTypes
        .filter((s) => validHeaders.includes(s.label.toLowerCase()))
        .map((sizeType) => {
          return (
            <div
              key={sizeType.value}
              onClick={() => setTypeCallback(sizeType.value)}
              className={clsx(
                'paragraph-sm m-auto cursor-pointer font-bold ',
                sizeType.value === current ? 'underline opacity-100' : 'opacity-60'
              )}
            >
              {sizeType.label}
            </div>
          )
        })}
    </div>
  )
}

const SelectSizeHeader = ({ validHeaders, selected, callback, type, pageType }) => {
  const selectHeading = pageType === 'gift-card' ? 'Select Amount:' : 'Selected Size:'
  return (
    <div className={'flex h-11 flex-row items-center justify-between md:h-auto'}>
      <p className={'paragraph-lg font-bold'}>
        {selectHeading} {selected.value ?? selected.name}
      </p>
      {pageType !== 'gift-card' ? (
        <SelectCountrySizeCategory validHeaders={validHeaders} current={type} setTypeCallback={callback} />
      ) : (
        <>
          <input name="gift-card-total" type="hidden" value={selected.value} />
        </>
      )}
    </div>
  )
}

const checkHeader = (parseSizeDataObjects) => {
  const keys = ['cm', 'us', 'eu', 'uk']
  const object = parseSizeDataObjects[0]
  const arr = keys.reduce((acc, key) => {
    if (object[key] !== undefined) {
      acc.push(key)
    }
    return acc
  }, [])

  return arr
}

const SectionRow = ({ children, title }) => {
  return (
    <div class="flex flex-col  gap-2 border-b border-solid border-b-neutrals-medium-grey py-4 ">
      <div className={'font-medium uppercase'}>{title}</div>
      <div className={'flex flex-row flex-wrap gap-2'}>{children}</div>
    </div>
  )
}

const SelectSizeOptions = ({ type, ageCategory, callback, selected, linkedOptions, productId }) => {
  const getFirstAvailableIndex = useMemo(() => {
    let index = 0
    for (const category of ageCategory.categories) {
      const ageGroup = ageCategory.groups[category]
      if (!ageGroup) {
        index += 1
        continue
      }
      break
    }
    return index
  }, [])

  return (
    <div class={'flex flex-col'}>
      {ageCategory?.categories?.map((category, index) => {
        const ageGroup = ageCategory?.groups[category]
        if (!ageGroup) return <></>

        const width = !selected?.width ? selected?.tmpWidth : selected?.width
        const displayOptions = width?.length > 0 ? ageGroup[width] : ageGroup
        if (!displayOptions?.length > 0) return <></>
        return (
          <SectionRow title={category}>
            {index === getFirstAvailableIndex && linkedOptions?.small ? (
              <div
                onClick={() => {
                  window.location.href = `/products/${linkedOptions?.small}`
                }}
                class="flex h-12 w-16 min-w-18 cursor-pointer flex-col items-center justify-center rounded-md border border-solid border-neutrals-tertiary-grey"
              >
                <span class="paragraph-xs text-center">Smaller Sizes</span>
              </div>
            ) : (
              <></>
            )}

            {displayOptions?.map((option) => {
              return (
                <div>
                  <SelectorSize productId={productId} selected={selected} type={type} option={option} handleSetSelectedCallback={callback} />
                </div>
              )
            })}
            {index >= ageCategory.categories.length - 1 && linkedOptions?.bigger && (
              <div
                onClick={() => {
                  window.location.href = `/products/${linkedOptions?.bigger}`
                }}
                class="flex h-12 w-16 min-w-18 cursor-pointer flex-col items-center justify-center rounded-md border border-solid border-neutrals-tertiary-grey"
              >
                <span class="paragraph-xs w-1/2 text-center">Bigger Sizes</span>
              </div>
            )}
          </SectionRow>
        )
      })}
    </div>
  )
}

const removeWidthKey = (input) => {
  const splitLastIndex = input.lastIndexOf('/')
  return input.slice(0, splitLastIndex)
}

const SelectWidthOptions = ({ parseSizeDataObjects, selected, handleSetSelected, widths, type, productId }) => {
  const handleUpdatedWidthCallback = (evt) => {
    const { dataset } = evt.currentTarget
    const getGetMatchingWidth = parseSizeDataObjects.filter((size) => size.width.includes(dataset.width))
    const findMatchingSizes = getGetMatchingWidth.find((o) => {
      return removeWidthKey(selected.variant).trim() === removeWidthKey(o.name).trim()
    })

    if (!findMatchingSizes) {
      handleSetSelected({
        id: '',
        name: '',
        width: dataset.width,
        firstRender: true,
        variant: '',
        unavailable_stock: false
      })
      return
    }
    const matchedSize = !findMatchingSizes ? parseSizeDataObjects[0] : findMatchingSizes
    const name = getSizeFromTitle(matchedSize, type)
    const unavailable = !matchedSize.available

    handleSetSelected({
      id: matchedSize.id,
      name,
      width: matchedSize.width,
      firstRender: false,
      variant: matchedSize.name,
      unavailable_stock: unavailable
    })
    handleSelectedVariantParam(matchedSize.id)
  }

  return (
    <SectionRow title="Width">
      {widths?.map((w) => {
        const hasStock = !!w?.hasStock
        const isSelected = w?.width === selected.width
        if (!hasStock) {
          return (
            <div
              data-width={w.width}
              class={`flex h-12 w-16 cursor-pointer flex-col items-center justify-center rounded-md border border-solid
                ${isSelected ? 'border-black bg-neutrals-medium-grey/70' : 'bg-neutrals-medium-grey/50'}
                `}
              onClick={handleUpdatedWidthCallback}
              data-dl-track="toggle_variant_pdp"
              data-dl-attribute="item_width"
              data-dl-value={w.width}
              data-dl-product-id={productId}
            >
              <s className={`${isSelected ? '' : 'opacity-50'}`}>{w.width}</s>
            </div>
          )
        }

        return (
          <div
            data-width={w?.width}
            class={`flex h-12 w-16 cursor-pointer flex-col items-center justify-center rounded-md border border-solid ${
              isSelected ? 'bg-primary-blue text-white' : 'border-neutrals-tertiary-grey '
            } `}
            onClick={handleUpdatedWidthCallback}
              data-dl-track="toggle_variant_pdp"
              data-dl-attribute="item_width"
              data-dl-value={w.width}
              data-dl-product-id={productId}
          >
            {w?.width}
          </div>
        )
      })}
    </SectionRow>
  )
}

/**
 *
 * If the product variant options has defaultValue (In our case variant width E) has a value and E has stock available
 * Re-sort the array to place E first and set width value to E
 *
 * If E does not exist or has no stock use the default logic of having the options with no stock to the end of the list.
 *
 * @param {{width: string, widthOptions: {width: string, hasStock: boolean}[]}} productsWidths
 * @param {string} defaultValue
 * @returns {{width: string, widthOptions: {width: string, hasStock: boolean}[]}}
 */
const handleHasDefaultValue = (productsWidths, defaultValue) => {
  const hasDefault = productsWidths.widthOptions.find((option) => option.width === defaultValue)
  if (!hasDefault) return productsWidths

  if (!hasDefault.hasStock) return productsWidths

  /** Disable the sorting of the width values
   *
   */
  const sortedWidthOptions = productsWidths.widthOptions.sort((a, b) => a.width.localeCompare(b.width))
  return {
    width: defaultValue,
    widthOptions: sortedWidthOptions
  }
}

/**
 * If we have a multiple Width options available
 * Check to see which width collection has stock
 */
const handleDisplayStockOption = (data) => {
  const getWidthsAvailability = data.reduce((acc, d) => {
    if (!d?.width) {
      return acc
    }
    if (acc[d.width]) {
      acc[d.width].push(d)
      return acc
    }
    acc[d.width] = [d]
    return acc
  }, {})

  const widthOptions = Object.keys(getWidthsAvailability)

  if (!widthOptions.length) {
    return {
      width: '',
      widthOptions: []
    }
  }

  if (widthOptions.length === 1) {
    return {
      width: widthOptions[0],
      widthOptions: widthOptions.map((w) => ({ width: w, widthOptions: [{ width: w, hasStock: true }] }))
    }
  }

  const widthStockObjects = widthOptions.map((w) => {
    const hasStock = getWidthsAvailability[w].some((size) => size.available)
    return { width: w, hasStock }
  })

  const widthWithStock = widthStockObjects.find((s) => s.hasStock)
  if (!widthWithStock) {
    return { width: widthOptions[0], widthOptions: widthOptions.map((w) => ({ width: w, hasStock: true })) }
  }
  const sortedWidthOptions = widthStockObjects.sort((a, b) => b.hasStock - a.hasStock)

  return { width: widthWithStock.width, widthOptions: sortedWidthOptions }
}

function ProductSelector(props) {
  const [type, setType] = useState(props.dataType || 'US')
  const parseSizeDataObjects = useMemo(() => {
    const variants = props.dataOptions
    const parseString = variants.replaceAll("'", '"').replaceAll('\n', '').trimStart().trimEnd()
    const data = JSON.parse(parseString)
    return data
  }, [])

  const productWidths = useMemo(
    () => handleHasDefaultValue(handleDisplayStockOption(parseSizeDataObjects), DEFAULT_WIDTH_IF_PRESENT),
    []
  )

  const [selected, setSelected] = useState(() => {
    const firstOption = productWidths.width
    return {
      id: '',
      name: '',
      width: '',
      tmpWidth: firstOption,
      firstRender: true,
      variant: '',
      unavailable_stock: false,
      value: ''
    }
  })
  const handleSetSelected = (value) => setSelected(() => ({ ...value, firstRender: false }))
  const handleSetType = (t) => setType(t)

  const ageCategory = useMemo(() => {
    const o = parseSizeDataObjects
    const validHeaders = checkHeader(o)
    const categories = Array.from(new Set(o.map((a) => a?.age_group)).values())
    /** Parse the variant data into their appropriate age groups and width sub-groups if present  */
    const groups = sizeGroups(o)
    return {
      categories,
      groups,
      validHeaders
    }
  }, [])

  useEffect(() => {
    if (!selected.id || selected.firstRender) {
      handleSetSelected(selected)
      return
    }
    /** Get Current size */
    const currentSize = parseSizeDataObjects.find((v) => v.id === selected.id)
    if (!currentSize) return
    /** Parse in the size and the new type (EU, US, UK) to get the new parsed value. */
    const sizeValue = handleGetTypeValue(type, currentSize)
    /** Update the selected state */
    handleSetSelected({
      id: selected.id,
      name: selected.name,
      width: selected.width,
      variant: selected.variant,
      unavailable_stock: selected.unavailable_stock,
      value: !sizeValue ? selected.value : sizeValue
    })
  }, [type])

  return (
    <div
      className={
        props.dataClass
          ? props.dataClass
          : 'border-t border-solid border-t-neutrals-light-grey bg-primary-white px-8 pb-4 pt-8'
      }
    >
      <input name="id" value={selected.id} type="hidden" />
      {selected.unavailable_stock && (
        <input name="unavailable_stock" value={`${selected?.value ?? selected.name}`} type="hidden" />
      )}
      <SelectSizeHeader
        validHeaders={ageCategory.validHeaders}
        selected={selected}
        type={type}
        callback={handleSetType}
        pageType={props?.dataPdpType}
      />
      <SelectSizeOptions
        productId={props?.dataProduct}
        type={type}
        selected={selected}
        ageCategory={ageCategory}
        callback={handleSetSelected}
        linkedOptions={{
          small: props?.dataSmall ?? '',
          bigger: props?.dataBigger ?? ''
        }}
      />
      {!!productWidths?.widthOptions?.length && productWidths.widthOptions.length > 1 && (
        <SelectWidthOptions
          widths={productWidths.widthOptions}
          handleSetSelected={handleSetSelected}
          selected={selected}
          type={type}
          productId={props?.dataProduct}
          parseSizeDataObjects={parseSizeDataObjects}
        />
      )}
    </div>
  )
}

export default function ProductSelectPreact({ data }) {}

register(ProductSelector, 'x-product-selector')
